import React, {useEffect, useState, useCallback, useRef} from 'react';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {Menu} from '../../utils/menu/Menu';
import {curatorsAction} from '../../actions/usersAction';
import cfg, {courses, formatErrors, roleAdmin, roleCurator, userPrintName, roleTechnicalSpecialist, roleChiefNutritionist, emptyUser} from '../../constants';
import {toast} from 'react-toastify';
import {InputSearch} from '../../utils/inputSearch/InputSearch';
import {SortItem} from '../../utils/sortItem/SortItem';
import {ButtonMore} from '../../utils/buttonMore/buttonMore';
import {Pagination} from '../../utils/pagination/Pagination';
import {clientBlockAction, clientsAction, clientsDeleteAction, manualActivationClientAction, manualSetCodeClientAction} from "../../actions/clientAction";
import {MenuHeader} from "../../utils/menu/MenuHeader";
import {InputCheckbox} from "../../utils/checkbox/InputCheckbox";
import {SortItemFilter} from "../../utils/sortItem/SortItemFilter";
import {ClientsCuratorModal} from "./utils/ClientsCuratorModal";
import {ConfirmModal} from "../../utils/confirm/ConfirmModal";
import {ClientsMessageModal} from "./utils/ClientsMessageModal";

const Clients = (props) => {
  const {user, isAdmin, getCurators, getClients, isTechnicalSpecialist, isChiefNutritionist} = props;
  const [users, setUsers] = useState([]);
  const [usersNames, setUsersNames] = useState({});
  const [courseList] = useState([{ key: 0, text: 'Все', value: 0 }].concat(courses));
  const [courseNames] = useState(courses.reduce((res, i) => {
    res[i.value] = i.text;
    return res;
  }, {}));
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({});
  const [checked, setChecked] = useState({});
  const [checkedAll, setCheckedAll] = useState(false);
  const abortRef = useRef(null);

  const handleChangeFilter = useCallback((field, value) => {
    setFilter((data) => {
      return {...data, [field]: value, page: 1}
    });
  }, []);

  const handleChangePage = useCallback((value) => {
    setFilter((data) => {
      return {...data, page: value}
    });
  }, []);

  const handleChangeSort = useCallback((sort, sortd) => {
    setFilter((data) => {
      return {...data, sort, sortd}
    });
  }, []);

  // get curators list
  const getCuratorsList = useCallback(() => {
    getCurators().then((data) => {
      let list = [{ key: '', text: 'Все', value: '' }];
      let names = {};
      (data || []).forEach((i) => {
        let name = userPrintName(i);
        list.push({key: i.id, text: name, value: i.id, role_id: i.role_id, status: i.status});
        names[i.id] = name;
      });
      setUsers(list);
      setUsersNames(names);
    }).catch(err => {
      toast.error(formatErrors([err.message]), {position: toast.POSITION.BOTTOM_RIGHT});
    });
  }, [getCurators]);

  // get clients list
  const getClientsList = useCallback((filter) => {
    if (abortRef && abortRef.current) {
      abortRef.current.abort();
    }
    const abortNew = new AbortController();
    abortRef.current = abortNew;
    getClients(filter, isAdmin, abortRef && abortRef.current ? abortRef.current.signal : null, isTechnicalSpecialist).then((data) => {
      abortRef.current = null;
      setItems(data.items || []);
      setTotal(data.total);
      setChecked({});
      setCheckedAll(false);
      localStorage.setItem('dc_currentClientsFilter', JSON.stringify(filter));
    }).catch(err => {
      if (err.name !== 'AbortError') {
        toast.error(formatErrors([err.message]), {position: toast.POSITION.BOTTOM_RIGHT});
      }
      abortRef.current = null;
    });
  }, [getClients, isAdmin, isTechnicalSpecialist]);

  const handleChecked = (id) => {
    let values = {...checked};
    if (values.hasOwnProperty(id) && values[id]) {
      delete values[id];
      setChecked(values);
      setCheckedAll(false);
    } else {
      values[id] = true;
      setChecked(values);
      setCheckedAll(items.filter(i => i.id !== id && !checked[i.id]).length === 0);
    }
  };

  const handleCheckedAll = () => {
    setCheckedAll(!checkedAll);
    setChecked(checkedAll ? {} : items.reduce((res, i) => {
      res[i.id] = true;
      return res;
    }, {}));
  };

  const handleDelete = (id) => {
    return new Promise((resolve, reject) => {
      if (loading) {
        return resolve(false);
      }
      setLoading(true);
      props.deleteClient(id, isAdmin, isTechnicalSpecialist).then(() => {
        setLoading(false);
        resolve(true);
        toast.success('Пользователь удален', {position: toast.POSITION.BOTTOM_RIGHT});
        getClientsList(filter);
      }).catch(err => {
        setLoading(false);
        toast.error(formatErrors([err.message]), {position: toast.POSITION.BOTTOM_RIGHT});
        reject(err);
      });
    });
  };

  const handleBlock = (id, block) => {
    return new Promise((resolve, reject) => {
      if (loading) {
        return resolve(false);
      }
      setLoading(true);
      props.blockClient(id, block, isAdmin, isTechnicalSpecialist).then(() => {
        setLoading(false);
        resolve(true);
        toast.success('Пользователь ' + (block ? 'заблокирован' : 'разблокирован'), {position: toast.POSITION.BOTTOM_RIGHT});
        getClientsList(filter);
      }).catch(err => {
        setLoading(false);
        toast.error(formatErrors([err.message]), {position: toast.POSITION.BOTTOM_RIGHT});
        reject(err);
      });
    });
  };

  const handleManualActivation = (id) => {
    return new Promise((resolve, reject) => {
      if (loading) {
        return resolve(false);
      }
      setLoading(true);
      props.manualActivationClient(id).then(() => {
        setLoading(false);
        resolve(true);
        toast.success('Пользователь активирован', {position: toast.POSITION.BOTTOM_RIGHT});
        getClientsList(filter);
      }).catch(err => {
        setLoading(false);
        toast.error(formatErrors([err.message]), {position: toast.POSITION.BOTTOM_RIGHT});
        reject(err);
      });
    });
  };

  const handleManualSetCode = (id) => {
    return new Promise((resolve, reject) => {
      if (loading) {
        return resolve(false);
      }
      setLoading(true);
      props.manualSetCodeClient(id).then(() => {
        setLoading(false);
        resolve(true);
        toast.success('Пользователь получил код для анкеты здоровья', {position: toast.POSITION.BOTTOM_RIGHT});
        getClientsList(filter);
      }).catch(err => {
        setLoading(false);
        toast.error(formatErrors([err.message]), {position: toast.POSITION.BOTTOM_RIGHT});
        reject(err);
      });
    });
  };

  // get clients list on change filter
  useEffect(() => {
    if (filter.page) {
      getClientsList(filter);
    }
  }, [filter, getClientsList]);

  useEffect(() => {
    if (!user || !user.id) {
      return;
    }
    const baseFilter = {page: 1, limit: 10, search: '', sort: 'email', sortd: 'asc', curator_id: '', course: 0};
    let curFilter = localStorage.getItem('dc_currentClientsFilter');
    curFilter = curFilter ? JSON.parse(curFilter) : baseFilter;
    curFilter.page = !curFilter.page ? 1 : curFilter.page;
    if (user.role_id === roleCurator) {
      curFilter.curator_id = user.id;
    } else {
      curFilter.curator_id = "";
    }
    getCuratorsList();
    setFilter(curFilter);
  }, [user, getCuratorsList]);

  return <div className="content">
    <Menu isStatic={(user.role_id === roleCurator || user.role_id === roleChiefNutritionist) ? false : true}/>

    {(isAdmin || isTechnicalSpecialist) && <div className="heading heading--flex">
      <h2 className="heading__title">Пользователи</h2>
      <div className="heading__group">

        <ClientsMessageModal checked={checked} users={users}
                             trigger={<button type="button" className="heading__btn heading__btn--transparent heading__btn--email"></button>}/>
        <ClientsCuratorModal checked={checked} users={users} onSuccess={() => getClientsList(filter)}
                             trigger={<button type="button" className="heading__btn heading__btn--transparent heading__btn--change modal-btn">Сменить куратора</button>}/>
      </div>

      {filter.page && filter.page > 0 && <InputSearch className="heading__input"
                                                          value={filter.search}
                                                          loading={loading}
                                                          handleChange={handleChangeFilter} />}
    </div>}
    {!isAdmin && !isTechnicalSpecialist && <MenuHeader>
      <React.Fragment>
        <h2 className="header__title">Пользователи <span>{total}</span></h2>
        <div className="heading__group">
          {!isChiefNutritionist && <ClientsMessageModal curatorId={user.id}
                               trigger={<button type="button" className="heading__btn heading__btn--transparent heading__btn--email"></button>}/>}
          {isChiefNutritionist && <ClientsMessageModal users={users}
                               trigger={<button type="button" className="heading__btn heading__btn--transparent heading__btn--email"></button>}/>}
        </div>
        {filter.page && filter.page > 0 && <InputSearch className="heading__input"
                                                        value={filter.search}
                                                        loading={loading}
                                                        handleChange={handleChangeFilter} />}
      </React.Fragment>
    </MenuHeader>}

    <div className="users">
      <div className="users__wrapper">
        <div className="sort">
          {(isAdmin || isTechnicalSpecialist) && <InputCheckbox name="check_all" checked={checkedAll} onChange={handleCheckedAll}/>}
          <SortItem title="ФИО" className="sort__item--name" sort="last_name" sortd={filter.sort === "last_name" ? filter.sortd : ""} onChange={handleChangeSort}/>
          <SortItem title="Телефон" className="sort__item--phone" sort="phone" sortd={filter.sort === "phone" ? filter.sortd : ""} onChange={handleChangeSort}/>
          <SortItem title="E-mail" className="sort__item--email" sort="email" sortd={filter.sort === "email" ? filter.sortd : ""} onChange={handleChangeSort}/>
          {!isAdmin && !isTechnicalSpecialist && <SortItem title="Куратор" className="sort__item--curator" sort="curator_id" sortd={filter.sort === "curator_id" ? filter.sortd : ""} onChange={handleChangeSort}/>}
          {(isAdmin || isTechnicalSpecialist) && <SortItemFilter title="Куратор" className="sort__item--curator" name="curator_id" value={filter.curator_id} onChange={handleChangeFilter} options={users.filter(i => i.role_id === roleCurator || !i.role_id)}/>}
          {!isAdmin && !isTechnicalSpecialist && <SortItem title="Курс" className="sort__item--course" sort="course" sortd={filter.sort === "course" ? filter.sortd : ""} onChange={handleChangeSort}/>}
          {(isAdmin || isTechnicalSpecialist) && <SortItemFilter title="Курс" className="sort__item--course" name="course" value={filter.course} onChange={handleChangeFilter} options={courseList} isNumeric={true}/>}
        </div>
        <div className="users__inner">
          {
            items && items.length > 0 && items.map((i) => <div key={i.id} className="users__item">
              {(isAdmin || isTechnicalSpecialist) && <InputCheckbox name={i.id} checked={checked[i.id] || false} onChange={() => handleChecked(i.id)}/>}
              <div className="users__ava">
                {i.photo && <img src={cfg.public_url + i.photo} alt="" />}
                {!i.photo && <div className="users__ava--none">{i.first_name.substring(0, 1)}{i.last_name.substring(0, 1)}</div>}
              </div>
              <span className="users__name">{i.last_name} {i.first_name} {i.patronymic_name}</span>
              <span className="users__phone">{i.phone}</span>
              <span className="users__email">{i.email}</span>
              <span className="users__curator users-curator-max-height">
                {i.curator_id && usersNames[i.curator_id] ? usersNames[i.curator_id] : ''}
                {i.curator_id_temp && <div className="users-curator-temp">Временный: {usersNames[i.curator_id_temp] ? usersNames[i.curator_id_temp] : ''}</div>}
                {i.curator_id === emptyUser && <div className="users-curator-temp">Технический специалист</div>}
              </span>
              <span className="users__course">{i.course && courseNames[i.course] ? courseNames[i.course] : ''}</span>
              {(isAdmin || isTechnicalSpecialist) && <ButtonMore>
                <ul className="dropdown">
                  {/*<li className="dropdown__item icon-edit">
                    <span>Редактировать</span>
                  </li>*/}
                  {i.block_clients && <li className="dropdown__item icon-mark">
                    <ConfirmModal trigger={<span>Разблокировать</span>} triggerWrap={false} title={"Разблокировать пользователя?"} handleOk={() => handleBlock(i.id, false)}/>
                  </li>}
                  {!i.block_clients && <li className="dropdown__item dropdown__act--block">
                    <ConfirmModal trigger={<span>Заблокировать</span>} triggerWrap={false} title={"Заблокировать пользователя?"} handleOk={() => handleBlock(i.id, true)}/>
                  </li>}
                  <li className="dropdown__item icon-delete">
                    <ConfirmModal trigger={<span>Удалить</span>} triggerWrap={false} title={"Удалить пользователя?"} handleOk={() => handleDelete(i.id)}/>
                  </li>
                  {(i.curator_id === "c3e51184-ba83-443f-b1bd-4100ff055c8c") && <li className="dropdown__item icon-mark">
                    <ConfirmModal trigger={<span>Ручная активация</span>} triggerWrap={false} title={"Ручная активация?"} handleOk={() => handleManualActivation(i.id)}/>
                  </li>}
                  {(i.code === "") && <li className="dropdown__item icon-mark">
                    <ConfirmModal trigger={<span>Ручная установка кода для анкеты здоровья</span>} triggerWrap={false} title={"Ручная установка кода для анкеты здоровья?"} handleOk={() => handleManualSetCode(i.id)}/>
                  </li>}
                </ul>
              </ButtonMore>}
            </div>)
          }
        </div>
      </div>
    </div>

    {filter.page && filter.page > 0 && <Pagination page={filter.page} total={total} limit={filter.limit} onChange={handleChangePage}/>}
  </div>;
};

const mapStateToProps = (state) => ({
  user: state.user.profile,
  isAdmin: state.user.profile && state.user.profile.role_id === roleAdmin,
  isTechnicalSpecialist: state.user.profile && state.user.profile.role_id === roleTechnicalSpecialist,
  isChiefNutritionist: state.user.profile && state.user.profile.role_id === roleChiefNutritionist,
});

const mapDispatchToProps = (dispatch) => {
  return {
    getClients(data, isAdmin, signal, isTechnicalSpecialist) {
      return dispatch(clientsAction(data, isAdmin, signal, isTechnicalSpecialist));
    },
    getCurators() {
      return dispatch(curatorsAction());
    },
    deleteClient(id, isAdmin, isTechnicalSpecialist) {
      return dispatch(clientsDeleteAction(id, isAdmin, isTechnicalSpecialist));
    },
    blockClient(id, block, isAdmin, isTechnicalSpecialist) {
      return dispatch(clientBlockAction(id, block, isAdmin, isTechnicalSpecialist));
    },
    manualActivationClient(id) {
      return dispatch(manualActivationClientAction(id));
    },
    manualSetCodeClient(id) {
      return dispatch(manualSetCodeClientAction(id));
    }
  }
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Clients))
